/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {ChangeEvent, useEffect, useState} from 'react';
import {makeStyles} from '@material-ui/core';
import FormControl from '@material-ui/core/FormControl';
import Autocomplete from '@material-ui/lab/Autocomplete';
import {useHasMounted} from '@ion/core/src/hooks/useHasMounted';
import {translate} from '@ion/core/src/locale/locale';
import {FormTextField} from '@ion/dls/src/components/forms/FormTextField';
import {Options} from '@ion/dls/src/components/menus/Dropdown';
import {dlsAlt, dlsColor, dlsZIndex} from '@ion/dls/src/theme/theme';
import {
  getOptionLabel,
  getOptionSelected
} from '@ion/features/src/helpers';

// defines the device identifier types chooser prop definitions ...
export type DeviceIdentifierTypeChooserProps = {
  carrierDeviceIdentifierTypesOnly?: boolean;
  onChange?: (option: Options) => void;
  readOnly?: boolean;
  required?: boolean;
  hasError?: boolean;
  classes?: string;
  errorText?: string;
  defaultValue?: string;
}

// defines the styles used by device types chooser ...
const noPadAndMargin = {
  padding : '0px !important',
  margin  : '0px !important'
};
const useStyles = makeStyles(theme => ({
  root : {
    ...noPadAndMargin
  },
  listbox : {
    ...noPadAndMargin
  },
  popper : {
    zIndex : dlsZIndex('deviceTypeChooserPopper')
  },
  paper : {
    padding : '0px !important'
  },
  option : {
    padding       : '17px !important',
    ...dlsAlt('components', 'dropdown'),
    color         : dlsColor('grey3'),
    textTransform : ({upperCase}: {upperCase: boolean}) => (
      upperCase ? 'uppercase' : 'none'
    ),
    letterSpacing : ({upperCase}: {upperCase: boolean}) => (
      upperCase ? dlsAlt('components', 'dropdown').letterSpacing : 'normal'
    ),
    '&[data-focus="true"]' : {
      backgroundColor : `${dlsAlt('components', 'icon', 'selected')} !important`
    }
  },
  endAdornment : {
    paddingRight : '7px !important'
  },
  input : {
    paddingLeft  : '7px !important',
    paddingRight : '3em !important'
  },
  inputRoot : {
    '&::before' : {
      borderBottom : 'none !important'
    },
    '&.Mui-focused' : {
      '&::before' : {
        borderBottom : `1px solid ${dlsColor('aaBlue')} !important`
      }
    }
  },
  formControl : {
    borderBottom           : ({hasError}: {hasError: boolean;}) => hasError ? 'none' : `1px solid ${dlsColor('grey5')}`,
    width                  : '55%',
    '& .MuiInputBase-root' : {
      width        : '100%',
      marginTop    : 0,
      border       : ({hasError}: {hasError: boolean;}) => hasError ? `solid 2px ${dlsColor('caRed')}` : 'none',
      borderBottom : 'none !important'
    }
  },
  popupIndicator : {
    '&:hover, &:active' : {
      backgroundColor : 'transparent'
    },
    '& .MuiTouchRipple-root' : {
      display : 'none'
    }
  },
  clearIndicator : {
    '&:hover, &:active' : {
      backgroundColor : 'transparent'
    },
    '& .MuiTouchRipple-root' : {
      display : 'none'
    },
    '& .MuiSvgIcon-root' : {
      width     : '0.65em',
      height    : '0.65em',
      '&:hover' : {
        color : dlsColor('caRed')
      }
    }
  }
}));

/**
 * Returns a list of supported CTC Device Identifier types suitable for a dropdown list.
 */
const getDeviceIdentifierTypes = (carrierTypesOnly: boolean): Options[] => {
  return translate('admin-devices:choosers.deviceIdentifierTypes.options', {returnObjects: true})
    .filter(option => carrierTypesOnly ? option.isCarrier : true)
    .map(({label, value}) => ({label, value})) as Options[] || [];
};

/**
 * Provides a utility which filters a list of Device Identifier Types.
 */
const getSelectedValue = (
  options: Options[],
  selected: string
): Options => {
  return options?.find((option: any) => option.label === selected)?.value;
};

/**
 * The DeviceIdentifierTypeChooser provides a list of Device Identifier types.
 *
 * ```typescript
 *
 *     import {DeviceIdentifierTypeChooser} from '@ion/features/src/entities/device';
 *
 *     // carrier device identifier types only
 *     <DeviceIdentifierTypeChooser carrierDeviceIdentifierTypesOnly
 *       />
 *
 *     // read-only mode ...
 *     <DeviceIdentifierTypeChooser readOnly
 *       />
 *
 *     // with default value and required check ...
 *     <DeviceIdentifierTypeChooser required
 *       />
 *
 *     // with callback subscription ...
 *     <DeviceIdentifierTypeChooser onChange={setDeviceIdentifierTypes}
 *       />
 *
 * ```
 *
 *
 * @carrierDeviceIdentifierTypesOnly Only include carrier device identifier types
 * @onChange                         Callback prop which will be invoked with the selected PEG Behavior
 * @readOnly                         When true, the component will be disabled/displayed in read-only mode;
 *                                   defaults to false
 * @required                         When true, the required indicator will be displayed
 * @validationError                  When true, the validationError indicator will be displayed
 * @classes                          Optional classes to apply to the wrapping element
 * @defaultValue                     Default value of the field
 */
export const DeviceIdentifierTypeChooser = ({
  onChange,
  readOnly,
  required,
  classes,
  errorText,
  defaultValue,
  carrierDeviceIdentifierTypesOnly = false,
  hasError = false
}: DeviceIdentifierTypeChooserProps) => {
  const _classes                  = useStyles({hasError});
  const [open, setOpen]           = useState(false);
  const [selected, setSelected]   = useState<string>(null);
  const [options, setOptions]     = useState<Options[]>([]);
  const hasMounted                = useHasMounted();

  useEffect(() => {
    if (hasMounted !== false) {
      const options = getDeviceIdentifierTypes(carrierDeviceIdentifierTypesOnly);
      setOptions(options);
      if (defaultValue) {
        onChange(getSelectedValue(options, defaultValue));
      } else if (selected) {
        onChange?.(undefined);
      }
    }
  }, []);

  useEffect(() => {
    if (defaultValue && !selected) {
      setSelected(defaultValue);
    }
  }, [defaultValue]);

  function handleFieldChange(e: ChangeEvent<HTMLInputElement>) {
    if (e) {
      e.persist();
    }
  };

  return (
    <div className={classes || _classes.root}>
      <FormControl className={_classes.formControl}>
        <Autocomplete disabled={readOnly}
          defaultValue={defaultValue || ''}
          autoComplete={false}
          clearOnBlur={false}
          openText=''
          closeText=''
          clearText={translate('admin-devices:choosers.deviceIdentifierTypes.clearText')}
          classes={{
            popper         : _classes.popper,
            paper          : _classes.paper,
            popupIndicator : _classes.popupIndicator,
            clearIndicator : _classes.clearIndicator,
            endAdornment   : _classes.endAdornment,
            input          : _classes.input,
            inputRoot      : _classes.inputRoot,
            listbox        : _classes.listbox,
            option         : _classes.option
          }}
          open={open}
          onOpen={function() {
            setOpen(true);
          }}
          onClose={function() {
            setOpen(false);
          }}
          getOptionSelected={getOptionSelected}
          getOptionLabel={getOptionLabel}
          onChange={onChange ? function(event, value: Options) {
            const val = value?.label;
            setSelected(val);
            onChange(value ? getSelectedValue(options, val) : null);
          } : null}
          options={options}
          loading={false}
          selectOnFocus={false}
          clearOnEscape
          noOptionsText={translate('admin-devices:choosers.deviceIdentifierTypes.noOptionsText')}
          onInputChange={handleFieldChange}
          renderInput={function(params) {
            return (
              <FormTextField {...params}
                autoComplete='off'
                error={hasError}
                helperText={hasError ? errorText || (required ? translate('common:editors.shared.required') : '') : ''}
                required={required}
                fieldLabel={translate('admin-devices:choosers.deviceIdentifierTypes.label')}
                placeholder={translate('admin-devices:choosers.deviceIdentifierTypes.placeholder')}
                name={translate('admin-devices:choosers.deviceIdentifierTypes.name')}
                InputProps={{
                  ...params.InputProps
                }} />
            );
          }} />
      </FormControl>
    </div>
  );
};

export default DeviceIdentifierTypeChooser;
